/* =========================================================================
 * THE SPLIT FAMILY — a "split into divisions" verdict is never an end state.
 *
 * Brian Kass, 2026-10-26, on PIAA 3A Boys Golf showing only a SPLIT INTO
 * DIVISIONS badge: "split the damn divisions - if there are 10 individual
 * champions - put them under the same tab - as we will create an indy ring
 * that encompasses all of them. and then for the team champion like La Salle
 * they also get a location here that surfaces them." Then: "its not just for
 * PIAA golf - its for the entire lot."
 *
 * So, for EVERY championship in the universe:
 *   · each TEAM champion (or self-standing division) is its own surfaced row,
 *     with its school, its rep and its actions;
 *   · ALL individual winners of one championship are ONE Individual grouping
 *     — one indy-ring opportunity listing every athlete with their school —
 *     never N separate opportunities;
 *   · the PARENT is a container. It never also counts as a crown, so a winner
 *     on the parent AND on the Team row is one opportunity, not two;
 *   · a split with no division rows, or a parent winner no row carries, is a
 *     visible FIX-IT with an action and a reason — never a badge with nothing
 *     behind it.
 *
 * Measured 2026-10-26T18:18Z over all 24,653 records: 95 families. 20 were
 * complete; 34 parents duplicated a child's winner, 24 parents carried a winner
 * no Team row held, 10 spread individuals over several records, 3 had uncrowned
 * divisions and 4 (all PIAA golf) were containers with no division rows at all.
 *
 * ⚠️ A FAMILY IS READ OFF THE NAMES, BECAUSE THAT IS WHAT THE OBJECT CARRIES.
 * Every splitter on this estate names a child "<parent name> — <division>"
 * (resolve.js#doSplit). There is no parent-link property on 2-64425303, and
 * `stage_note` (which does name the parent id) is not in the board projection.
 * An exact-name match against a record that exists is the narrowest link the
 * data supports; a prefix with no such record links nothing.
 *
 * PURE. No React, no fetch. `verify-split-family.mjs` drives it.
 * ========================================================================= */

import { athletesOf } from '../../../lib/championDisplay.js'

const S = (v) => (v == null ? '' : String(v)).trim()
export const SEP = ' — '

/** The words that make a division an INDIVIDUAL title. Mirrors the backend's
 *  INDIVIDUAL_MARKS (individual-champion.js), applied to the division label. */
const INDIVIDUAL = [
  /\bindividual\b/i, /\bmedalist\b/i, /\bsingles\b/i, /\bdoubles\b/i,
  /\ball-?around\b/i, /\bno\.?\s*[1-9]\b/i, /\bweight\s*class\b/i, /\b\d{2,3}\s*(?:lbs?|pounds)\b/i,
]

/** 'individual' | 'team' | 'division' for one division label. */
export function divisionRole(label) {
  const s = S(label)
  if (INDIVIDUAL.some((re) => re.test(s))) return 'individual'
  if (/\bteam\b/i.test(s)) return 'team'
  return 'division'
}

const nameOf = (c) => S(c?.name ?? c?.championship_name)
const champOf = (c) => S(c?.champion ?? c?.current_champion) || S(c?.winningSchool ?? c?.sr_winning_school)
const tagOf = (c) => S(c?.championSearchTag ?? c?.sr_champion_search)
const idOf = (c) => S(c?.id ?? c?.hs_object_id)
const realRep = (v) => { const s = S(v); return s && s.toLowerCase() !== 'unassigned' ? s : '' }
/** Mirrors individual-champion.js SCHOOL_SEGMENTS on the backend. */
const SCHOOL_SEGMENTS = new Set(['HS State', 'College', 'Conference', 'Canada'])

/** Equality after trim, case and whitespace normalization only. A shared prefix
 * or school abbreviation is not evidence that two schools are the same. */
export function sameSchool(a, b) {
  const key = (v) => S(v).toLowerCase().replace(/\s+/g, ' ')
  return Boolean(key(a)) && key(a) === key(b)
}

/* ---- ATHLETES ------------------------------------------------------------ */

// Preserve the shared parser's unreadable distinction; callers must carry its
// error as a fix, rather than treating this empty view as absence of evidence.
const refsOf = (c) => { const parsed = athletesOf(c); return parsed.ok ? parsed.lines : [] }

/**
 * The athletes one individual record carries, each with its school.
 * Reads `champion_references` first (the 2026-10-19 contract). A legacy blob
 * "Athlete (School)" is READ as one athlete at that school — never written
 * back, so a bad parse cannot reach a record. A school crowned with no athlete
 * named is still a winner of the grouping: listed by school, athlete pending.
 */
export function athletesOn(c, event = '') {
  if (!athletesOf(c).ok) return []
  const refs = refsOf(c)
  const school = S(c?.winningSchool ?? c?.sr_winning_school) || S(c?.champion ?? c?.current_champion)
  const src = S(c?.championSourceUrl ?? c?.champion_source_url) || null
  if (refs.length) {
    return refs.map((r) => ({ athlete: S(r.athlete), event: S(r.event) || event,
      school: S(r.school) || school, source: S(r.source) || src, recordId: idOf(c) }))
  }
  const champ = S(c?.champion ?? c?.current_champion)
  /* §3b of the 2026-10-19 contract: on a non-school segment (pro, equestrian,
   * open events) there is no school — the champion IS the athlete. Reading it
   * as a school would list "Payton Haynes / Zack Aiken" as an account. */
  const seg = S(c?.segment ?? c?.sr_segment)
  const schoolSegment = !seg || SCHOOL_SEGMENTS.has(seg)
  if (!schoolSegment) return champ ? [{ athlete: champ, event, school: '', source: src, recordId: idOf(c) }] : []
  const m = champ.match(/^(.*\S)\s*\(([^()]{2,80})\)\s*$/)
  if (m) return [{ athlete: m[1], event, school: m[2], source: src, recordId: idOf(c), fromBlob: true }]
  if (champ) return [{ athlete: '', event, school: champ, source: src, recordId: idOf(c) }]
  return []
}

/* ---- THE FAMILIES -------------------------------------------------------- */

/**
 * familiesOf(records) → Map<parentId, family>
 *
 * family = { parent, teams[], divisions[], individuals[], indy, fixes[] }
 *   teams/divisions  the surfaced rows (one opportunity each)
 *   individuals      the records whose winners go under the ONE Individual tab
 *   indy             the grouping: { members, athletes, schools, uncrowned }
 *   fixes            what stops this family being complete, each with an
 *                    ACTION and a REASON (no dead-end flags)
 */
// Name-derived display grouping only, never durable lineage or action authority.
const fieldValue=(record,keys)=>keys.map(key=>S(record?.[key])).find(Boolean)||''
const familyCompatible=(parent,child)=>[
  ['governingBody','governing_body','sr_governing_body'], ['sport','sr_sport'],
  ['seasonYear','season','sr_season_year'], ['segment','sr_segment'],
  ['level','sr_level'], ['state','sr_state'], ['scope','sr_scope'], ['gender','sr_gender'],
].every(keys=>{const a=fieldValue(parent,keys),b=fieldValue(child,keys);return !a||!b||a===b})
function parentCandidates(name){
  const i=name.lastIndexOf(SEP)
  if(i<=0)return []
  const base=name.slice(0,i),division=name.slice(i+SEP.length)
  const embedded=/^(Team|Individual)( · .+)$/.exec(division)
  return [{name:base,label:division},...(embedded?[{name:base+embedded[2],label:embedded[1]}]:[])]
}

export function familiesOf(records = []) {
  const byName = new Map()
  for (const c of records) { const n = nameOf(c); if (n) byName.set(n, byName.has(n) ? null : c) }
  const fams = new Map()
  const ensure = (p) => {
    const id = idOf(p)
    if (!fams.has(id)) fams.set(id, { parent: p, teams: [], individuals: [], divisions: [] })
    return fams.get(id)
  }
  for (const c of records) {
    const n = nameOf(c)
    const interpretations=parentCandidates(n)
    const duplicateInterpretation=interpretations.some(candidate=>byName.has(candidate.name)&&byName.get(candidate.name)===null)
    const candidates=interpretations.map(candidate=>({...candidate,parent:byName.get(candidate.name)}))
      .filter(candidate=>candidate.parent&&idOf(candidate.parent)!==idOf(c)&&familyCompatible(candidate.parent,c))
    if(duplicateInterpretation||candidates.length>1){
      for(const candidate of candidates)ensure(candidate.parent).ambiguousRelationship=true
      continue
    }
    if(candidates.length!==1)continue
    const {parent:p,label}=candidates[0]
    const role = divisionRole(label)
    const f = ensure(p)
    const entry = { record: c, label, role }
    if (role === 'individual') f.individuals.push(entry)
    else if (role === 'team') f.teams.push(entry)
    else f.divisions.push(entry)
  }
  const parentNames = new Set(records.flatMap(c=>parentCandidates(nameOf(c)).map(candidate=>candidate.name)))
  for (const c of records) {
    if (byName.get(nameOf(c)) === null && (parentNames.has(nameOf(c)) || tagOf(c) === 'split_into_divisions')) ensure(c).ambiguousParent = true
    else if (tagOf(c) === 'split_into_divisions') ensure(c)
  }

  /* ⚠️ TWO PARENTS, ONE NAME (festival-records-and-the-splitter, defect 4).
   * Rows attach to the FIRST record of a name; a second copy tagged split
   * then looks like an empty container. Saying "record the champions" there
   * would send a rep to re-do work that exists — so it is named for what it
   * is: a duplicate parent. */
  const withRows = new Map()
  for (const f of fams.values()) if (f.teams.length + f.divisions.length + f.individuals.length) withRows.set(nameOf(f.parent), f)
  for (const f of fams.values()) {
    const twin = withRows.get(nameOf(f.parent))
    if (twin && twin !== f && !(f.teams.length + f.divisions.length + f.individuals.length)) f.duplicateOf = idOf(twin.parent)
  }

  for (const f of fams.values()) {
    const athletes = f.individuals.flatMap((e) => athletesOn(e.record, e.label))
    const schools = [...new Set(athletes.map((a) => a.school).filter(Boolean))]
    f.indy = f.individuals.length ? {
      members: f.individuals.map((e) => idOf(e.record)),
      athletes,
      schools,
      uncrowned: f.individuals.filter((e) => !champOf(e.record) && !refsOf(e.record).length).length,
    } : null
    f.fixes = fixesFor(f)
  }
  return fams
}

/**
 * What stops a family being complete. Every entry carries the action that
 * resolves it and the reason it matters — the no-dead-end-flags rule.
 */
export function fixesFor(f) {
  const out = []
  for (const entry of f.individuals) {
    const parsed = athletesOf(entry.record)
    const athletes = athletesOn(entry.record, entry.label)
    const segment = S(entry.record?.segment ?? entry.record?.sr_segment)
    const needsSchool = !segment || SCHOOL_SEGMENTS.has(segment)
    if (!parsed.ok || athletes.some((a) => !a.athlete || !a.event || (needsSchool && !a.school))) {
      out.push({ key: 'indy_incomplete', action: 'Review the individual winner references',
        reason: !parsed.ok ? 'Individual winner references are unreadable; repair the evidence before quoting the indy ring.'
          : 'An individual winner is missing an athlete name, event or required school; the indy ring is not complete.' })
    }
  }
  const rows = [...f.teams, ...f.divisions]
  const kids = rows.length + f.individuals.length
  const parentChamp = champOf(f.parent)
  if (f.ambiguousParent) return [{ key: 'ambiguous_parent', action: 'Review the duplicate championship identities',
    reason: 'More than one record has this exact parent name. No child is attached by guess; verify the actual relationship before a split action.' }]
  if (f.ambiguousRelationship) return [{ key: 'ambiguous_relationship', action: 'Review the split championship relationship',
    reason: 'More than one parent interpretation is possible for a division name. The ambiguous row is not attached; verify its full championship identity before a split action.' }]
  if (!kids && f.duplicateOf) {
    out.push({ key: 'duplicate_parent', action: 'Check this duplicate of the championship',
      reason: `Another record with this exact name (${f.duplicateOf}) already holds the Team rows and the Individual grouping. `
        + 'This copy holds none, so it is a duplicate to reconcile, not work to redo.' })
    return out
  }
  if (!kids) {
    out.push({ key: 'split_unrecorded', action: 'Review the split championship records',
      reason: 'Marked as split into divisions, but its Team and Individual rows could not be resolved in this view. '
        + 'Check the full records and their identities before recording a winner or quoting.' })
  }
  if (parentChamp && kids) {
    const onARow = rows.some((e) => sameSchool(champOf(e.record), parentChamp))
    if (!onARow) {
      out.push({ key: 'parent_winner_unplaced',
        action: rows.length ? `Check ${parentChamp} against the Team row` : `Record ${parentChamp} as the Team champion`,
        reason: `The parent record still names ${parentChamp}, and no Team row carries it. `
          + 'Left on the parent it would be counted as a second crown, or lost when the parent stops counting.' })
    }
  }
  const unCrowned = rows.filter((e) => !champOf(e.record)).length
  if (unCrowned) {
    out.push({ key: 'division_uncrowned',
      action: `Find the ${unCrowned === 1 ? 'champion' : `${unCrowned} champions`} still missing`,
      reason: `${unCrowned} division row${unCrowned === 1 ? ' has' : 's have'} no winner recorded.` })
  }
  if (f.indy && f.indy.uncrowned) {
    out.push({ key: 'indy_incomplete', action: 'Record the remaining individual winners',
      reason: `${f.indy.uncrowned} individual title${f.indy.uncrowned === 1 ? ' has' : 's have'} no winner yet, `
        + 'so the indy ring cannot be quoted in full.' })
  }
  return out
}

/** Lookup by any member id → { family, role }. */
export function familyIndex(fams) {
  const idx = new Map()
  for (const [pid, f] of fams) {
    idx.set(pid, { family: f, role: 'parent' })
    for (const e of f.teams) idx.set(idOf(e.record), { family: f, role: 'team' })
    for (const e of f.divisions) idx.set(idOf(e.record), { family: f, role: 'division' })
    for (const e of f.individuals) idx.set(idOf(e.record), { family: f, role: 'individual' })
  }
  return idx
}

/** A compact, serialisable view of a family for a row to render. */
export function familyView(f) {
  if (!f) return null
  const row = (e) => ({ id: idOf(e.record), label: e.label, champion: champOf(e.record),
    rep: realRep(e.record?.rep ?? e.record?.sr_assigned_rep) })
  return {
    relationshipBasis: 'exact_unique_name',
    parentId: idOf(f.parent),
    parentName: nameOf(f.parent),
    teams: [...f.teams, ...f.divisions].map(row),
    indy: f.indy ? { members: f.indy.members, athletes: f.indy.athletes, schools: f.indy.schools,
      uncrowned: f.indy.uncrowned } : null,
    fixes: f.fixes,
  }
}

/**
 * Stamp `splitFamily` onto every record that belongs to one. Returns a NEW
 * array; the inputs are not mutated. Same shape as attachCitationFanout.
 */
export function attachSplitFamilies(records = []) {
  const fams = familiesOf(records)
  if (!fams.size) return records
  const idx = familyIndex(fams)
  const views = new Map([...fams].map(([id, f]) => [id, familyView(f)]))
  return records.map((c) => {
    const hit = idx.get(idOf(c))
    if (!hit) return c
    return { ...c, splitFamily: { role: hit.role, ...views.get(idOf(hit.family.parent)) } }
  })
}

/* ---- THE WORKLIST -------------------------------------------------------- */

const WORKED = new Set(['Outreach In Progress', 'Outreach Completed', 'Won', 'Champion Crowned'])

/**
 * collapseForWorklist(book, universe?) → the book as WORK, not as storage.
 *
 *   · a family PARENT never enters as a crown. If something is unfinished it
 *     enters as ONE fix-it item carrying the action and the reason; if the
 *     family is complete it does not enter at all (its rows do);
 *   · TEAM and DIVISION rows enter as themselves;
 *   · every INDIVIDUAL record of one championship collapses into ONE indy-ring
 *     item listing every athlete and school.
 *
 * `universe` is the whole loaded object, so a family is found even when the
 * book is one rep's slice. Nothing is dropped silently: `collapsed` reports
 * how many records were folded and how many parents were held as containers.
 */
export function collapseForWorklist(book = [], universe = null) {
  const fams = familiesOf(universe && universe.length ? universe : book)
  const zero = { folded: 0, containers: 0, groupings: 0, fixes: 0 }
  if (!fams.size) return { book, collapsed: zero }
  const idx = familyIndex(fams)
  const out = []
  const emitted = new Set()
  const n = { ...zero }
  for (const c of book) {
    const hit = idx.get(idOf(c))
    if (!hit) { out.push(c); continue }
    const f = hit.family
    const pid = idOf(f.parent)
    if (hit.role === 'team' || hit.role === 'division') {
      out.push({ ...c, splitFamily: { role: hit.role, ...familyView(f) } })
      continue
    }
    if (hit.role === 'individual') {
      n.folded += 1
      if (emitted.has(`indy:${pid}`)) continue
      emitted.add(`indy:${pid}`)
      n.groupings += 1
      out.push(indyItem(f, c, book))
      continue
    }
    n.containers += 1
    if (f.fixes.length && !emitted.has(`fix:${pid}`)) {
      emitted.add(`fix:${pid}`)
      n.fixes += 1
      out.push(fixItem(f, c))
    }
  }
  return { book: out, collapsed: n }
}

function indyItem(f, seed, book) {
  const p = f.parent
  const allowed = new Map(book.map((record) => [idOf(record), record]))
  const members = f.individuals.map((e) => allowed.get(idOf(e.record))).filter(Boolean)
  const a = members.flatMap((member) => athletesOn(member))
  const schools = [...new Set(a.map((entry) => entry.school).filter(Boolean))]
  const allWorked = members.length > 0 && members.every((m) => WORKED.has(S(m?.pursuit)))
  const scopedFamily = { ...f, teams: [], divisions: [], individuals: f.individuals.filter((e) => allowed.has(idOf(e.record))),
    indy: { members: members.map(idOf), athletes: a, schools, uncrowned: members.filter((m) => !champOf(m)).length }, fixes: [] }
  scopedFamily.fixes = fixesFor({ ...scopedFamily, parent: {} })
  return {
    ...seed,
    id: `indy:${idOf(p)}`,
    indyGroupOf: idOf(p), isIndyGroup: true, memberRecords: members,
    name: `${nameOf(p)}${SEP}Individual (indy ring)`,
    // This is a display summary, not an assigned record or an action target.
    rep: '', supportRep: '', supportRep2: '',
    pursuit: allWorked ? 'Outreach In Progress' : S(seed?.pursuit),
    champion: !scopedFamily.fixes.length && a.length ? `${a.length} individual winner${a.length === 1 ? '' : 's'}` : '',
    winningSchool: '', references: a,
    splitFamily: { role: 'individual', ...familyView(scopedFamily) },
    worklistAction: {
      label: 'Choose a member record for this indy ring',
      reason: `${members.length} records from this worklist are grouped for review${schools.length ? ` across ${schools.join(', ')}` : ''}. `
        + 'Select one actual record before an action. Each record keeps its own assignee, account and title gates; this summary grants no access.',
    },
  }
}

function fixItem(f, parentInBook) {
  const p = f.parent
  const first = f.fixes[0]
  return {
    ...parentInBook,
    id: idOf(p),
    isSplitFix: true,
    /* A fix-it is not a crown. A blank champion keeps it out of JUST_CROWNED;
     * its own tier puts it where a rep sees it. */
    champion: '',
    winningSchool: '',
    /* The container stamp ("CONTAINER: this event crowns N…") lives in
     * champion_detected_raw; read by championVoice on a blank champion it
     * would render as a WITHDRAWN crown. A fix-it is not a retraction. */
    championDetectedRaw: '',
    splitFamily: { role: 'parent', ...familyView(f) },
    worklistAction: { label: first.action, reason: f.fixes.map((x) => x.reason).join(' ') },
  }
}

/* ---- PAIRS --------------------------------------------------------------- */

/**
 * Legacy proposed pair labels (not authorization): Philip/Madison and
 * Zach/Alexis. The
 * pair names are display policy only, never proof of assignment or book access.
 * seatFor requires explicit authoritative support fields on each record.
 */
export const STANDING_PAIRS = Object.freeze({
  'Philip Davanzo III': 'Madison Gill',
  'Zach Muller': 'Alexis Usery',
})

/** Does this record belong on `person`'s worklist, and in which seat? */
export function seatFor(c, person) {
  const who = S(person).toLowerCase()
  if (!who) return null
  const lead = realRep(c?.rep) || realRep(c?.leadRep)
  if (lead.toLowerCase() === who) return 'lead'
  for (const k of ['supportRep', 'supportRep2']) if (S(c?.[k]).toLowerCase() === who) return 'support'
  return null
}
